// The seat's brief: "who you are, what you know, what you want, what you would rather not
// say and to whom" — the same markdown that drove the real run a demo was saved from,
// rendered. Shared between `CharacterSheet.tsx` (the full-screen route,
// `/plans/:planId/sheet`) and `DecisionsPanel.tsx`'s "about" drawer, because both show
// exactly the same thing for exactly the same plan; a second copy of this fetch would just
// be the same three states written twice.
//
// WHICH SEAT'S BRIEF TO SHOW is not a fact `planId` alone carries, so this asks the server:
// `client.readDemoSource(planId)`, which is `demo_plan_source` (migration 0028) read back
// as `GET /v1/plans/:planId/demo/source`. That is what replaced `demo-stash.ts`'s per-tab
// `sessionStorage`: the same plan opened from a second device or a fresh window still finds
// its seat, because the record lives on the plan rather than in one browser's storage. A
// plan with no source recorded — because it is not a demo, or because it predates
// migration 0028 — has nothing to show here, and that is not an error: both callers treat
// this as a convenience on top of a plan that opens normally either way.

import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { DemoSource } from '../api/contract.js';
import { Markdown } from '../markdown.js';

export type CharacterBriefProps = {
  /** Which set of styles to render the brief under. The full-page screen keeps `sheet`;
   *  the drawer passes its own, which is sized and padded like the panel beside it rather
   *  than like a page. */
  className?: string;
  client: ApiClient;
  planId: string;
};

export function CharacterBrief({
  client,
  planId,
  className = 'sheet',
}: CharacterBriefProps): JSX.Element | null {
  const [seat, setSeat] = useState<DemoSource | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // Distinct from `seat === null`, which is also true while the read is still in flight —
  // this only becomes true once the server has actually answered "nothing recorded here".
  const [sourceMissing, setSourceMissing] = useState(false);

  useEffect(() => {
    if (planId === '') {
      setSourceMissing(true);
      return;
    }
    let live = true;
    client
      .readDemoSource(planId)
      .then((source) => {
        if (!live) {
          return;
        }
        setSeat(source);
        client
          .readDemoSheet(source.scenarioId, source.seatLabel)
          .then((sheet) => {
            if (live) {
              setText(sheet);
            }
          })
          .catch(() => {
            if (live) {
              setProblem('That didn’t load. Try again in a moment.');
            }
          });
      })
      .catch(() => {
        if (live) {
          setSourceMissing(true);
        }
      });
    return () => {
      live = false;
    };
  }, [client, planId]);

  if (sourceMissing) {
    return <p className="screen__empty">There’s no character sheet to show here.</p>;
  }
  if (problem !== null) {
    return (
      <p className="screen__problem" role="status">
        {problem}
      </p>
    );
  }
  if (seat === null || text === null) {
    return null;
  }
  return <Markdown className={className} text={text} />;
}
